import ReactMarkdown, { defaultUrlTransform, type Components } from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import remarkGfm from "remark-gfm";

import { MermaidBlock } from "./MermaidBlock";

interface MarkdownContentProps {
  content: string;
}

// react-markdown's default URL sanitizer strips every `data:` URL, which would
// blank out inline base64 images (notebook image uploads embed as data URLs).
// Allow raster data-images through — but never `image/svg+xml`, whose markup
// can carry script — and defer to the default sanitizer for everything else.
const SAFE_DATA_IMAGE = /^data:image\/(png|jpe?g|gif|webp|avif);base64,/i;

function urlTransform(url: string): string {
  return SAFE_DATA_IMAGE.test(url) ? url : defaultUrlTransform(url);
}

const components: Components = {
  code({ className, children, ...rest }) {
    if (className?.includes("language-mermaid")) {
      return <MermaidBlock chart={String(children).trim()} />;
    }
    return (
      <code className={className} {...rest}>
        {children}
      </code>
    );
  },
  // Open all links (Composio connect URLs, etc.) in a new tab so the user
  // doesn't navigate away from the app. Mirrors the security attributes used
  // for integration buttons + user-message linkify in MessageBubble.tsx.
  a({ children, ...rest }) {
    return (
      <a {...rest} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  },
  // Wide tables scroll inside the bubble instead of stretching it.
  table({ children, ...rest }) {
    return (
      <div className="max-w-full overflow-x-auto">
        <table {...rest}>{children}</table>
      </div>
    );
  },
};

export function MarkdownContent({ content }: MarkdownContentProps) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeHighlight]}
      urlTransform={urlTransform}
      components={components}
    >
      {content}
    </ReactMarkdown>
  );
}
